import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CalendarIcon,
  UserCircleIcon,
  AcademicCapIcon,
  IdentificationIcon,
  GlobeAltIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { clinicianAPI, PublicClinicianProfile } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import VideoEmbed from './VideoEmbed';
import { apiErrorMessage } from '../../utils/apiError';

const formatPrice = (p?: number | string | null): string | null => {
  if (p === null || p === undefined || p === '') return null;
  const n = Number(p);
  return Number.isNaN(n) ? null : `$${n.toFixed(0)}`;
};

const ClinicianProfilePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [clinician, setClinician] = useState<PublicClinicianProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchClinician = async () => {
      if (!slug) {
        setError('Invalid clinician profile');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const data = await clinicianAPI.getClinicianBySlug(slug);
        setClinician(data);
      } catch (err: any) {
        console.error('Error fetching clinician:', err);
        if (err.response?.status === 404) {
          setError('Clinician not found');
        } else {
          setError(apiErrorMessage(err, 'Failed to load clinician profile'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchClinician();
  }, [slug]);

  // Handle book appointment click
  const handleBookAppointment = () => {
    if (!isAuthenticated) {
      // Redirect to login with return URL pointing to booking page
      navigate(`/login?redirect=/patient/book/${clinician?.booking_slug}`);
    } else if (user?.role === 'patient' && clinician) {
      // Phase 4: Navigate via URL path parameter (refresh-safe, bookmarkable)
      // Enterprise pattern: /patient/book/:slug (Zocdoc, Epic MyChart, Doctolib)
      navigate(`/patient/book/${clinician.booking_slug}`);
    } else {
      // Members and admins can't book appointments
      navigate('/');
    }
  };

  // Get back link
  const getBackLink = () => {
    return '/clinicians';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-clinic-surface flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-clinic-primary mx-auto" />
          <p className="mt-4 text-clinic-muted">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !clinician) {
    return (
      <div className="min-h-screen bg-clinic-surface">
        <header className="bg-white shadow-clinic-sm border-b border-clinic-primary-lighter">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Link to={getBackLink()} className="inline-flex items-center text-clinic-muted hover:text-clinic-primary transition-colors">
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Directory
            </Link>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-card shadow-clinic p-12 text-center">
            <UserCircleIcon className="mx-auto h-16 w-16 text-clinic-muted" />
            <h2 className="mt-4 text-xl font-semibold text-clinic-dark font-body">
              {error || 'Clinician not found'}
            </h2>
            <p className="mt-2 text-clinic-muted">
              This clinician profile may not be available or the link may be incorrect.
            </p>
            <Button
              as={Link}
              to="/clinicians"
              className="mt-6"
            >
              Browse All Clinicians
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-clinic-surface">
      {/* Header */}
      <header className="bg-white shadow-clinic-sm border-b border-clinic-primary-lighter">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to={getBackLink()} className="inline-flex items-center text-clinic-muted hover:text-clinic-primary transition-colors">
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Directory
            </Link>
            {!isAuthenticated && (
              <Link
                to="/login"
                className="text-sm text-clinic-primary hover:underline"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-card shadow-clinic overflow-hidden">
          {/* Profile Header */}
          <div className="bg-gradient-to-br from-clinic-primary to-clinic-primary-dark px-5 py-8 sm:px-8 sm:py-12">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              {/* Photo */}
              <div className="flex-shrink-0">
                {clinician.profile_photo_url ? (
                  <img
                    src={clinician.profile_photo_url}
                    alt={`${clinician.first_name} ${clinician.last_name}`}
                    className="w-32 h-32 rounded-full object-cover border-4 border-clinic-cream shadow-lg"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full bg-clinic-cream flex items-center justify-center shadow-lg">
                    <span className="text-4xl font-bold text-clinic-primary">
                      {clinician.first_name[0]}{clinician.last_name[0]}
                    </span>
                  </div>
                )}
              </div>

              {/* Name and Professional Title */}
              <div className="text-center sm:text-left min-w-0">
                <h1 className="text-2xl sm:text-3xl font-bold text-clinic-cream font-body break-words">
                  {clinician.first_name} {clinician.last_name}
                </h1>
                <p className="mt-2 text-lg text-clinic-cream/80">{clinician.specialty}</p>
                {clinician.specialties && clinician.specialties.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2 justify-center sm:justify-start">
                    {clinician.specialties.map((tag) => (
                      <span key={tag} className="inline-flex items-center rounded-full bg-clinic-cream/20 text-clinic-cream text-xs font-medium px-3 py-1">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Profile Content — two columns: About/video/services (left), fast facts (right) */}
          <div className="px-5 py-6 sm:px-8 sm:py-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left: About + video + services */}
              <div className="lg:col-span-2 space-y-8">
                {clinician.profile_bio && (
                  <div>
                    <h2 className="text-lg font-semibold text-clinic-dark font-body mb-3">About</h2>
                    <p className="text-clinic-muted whitespace-pre-wrap leading-relaxed">
                      {clinician.profile_bio}
                    </p>
                  </div>
                )}

                {clinician.video_intro_url && (
                  <div>
                    <h2 className="text-lg font-semibold text-clinic-dark font-body mb-3">Intro Video</h2>
                    <VideoEmbed url={clinician.video_intro_url} title={`${clinician.first_name} ${clinician.last_name} — intro`} />
                  </div>
                )}

                {clinician.services && clinician.services.length > 0 && (
                  <div>
                    <h2 className="text-lg font-semibold text-clinic-dark font-body mb-4">Services</h2>
                    <div className="space-y-3">
                      {clinician.services.map((s) => (
                        <div key={s.id} className="rounded-card border border-clinic-primary-lighter p-4">
                          <div className="flex items-start justify-between gap-3">
                            <p className="font-medium text-clinic-dark">{s.name}</p>
                            <div className="flex items-center gap-3 flex-shrink-0 text-sm text-clinic-muted">
                              <span className="inline-flex items-center"><ClockIcon className="h-4 w-4 mr-1" />{s.duration_minutes} min</span>
                              {formatPrice(s.price) && <span className="font-medium text-clinic-dark">{formatPrice(s.price)}</span>}
                            </div>
                          </div>
                          {s.description && <p className="text-sm text-clinic-muted mt-1.5">{s.description}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right: fast facts */}
              <aside className="lg:col-span-1">
                <div className="rounded-card border border-clinic-primary-lighter bg-clinic-cream/40 p-5 space-y-5">
                  <div className="flex items-start">
                    <AcademicCapIcon className="h-6 w-6 text-clinic-primary mr-3 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wide text-clinic-muted">Professional Title</p>
                      <p className="font-medium text-clinic-dark break-words">{clinician.specialty}</p>
                    </div>
                  </div>

                  <div className="flex items-start">
                    <IdentificationIcon className="h-6 w-6 text-clinic-primary mr-3 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wide text-clinic-muted">License Number</p>
                      <p className="font-medium text-clinic-dark break-words">{clinician.license_number}</p>
                    </div>
                  </div>

                  {clinician.website_url && (
                    <div className="flex items-start">
                      <GlobeAltIcon className="h-6 w-6 text-clinic-primary mr-3 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs uppercase tracking-wide text-clinic-muted">Website</p>
                        <a href={clinician.website_url} target="_blank" rel="noopener noreferrer" className="font-medium text-clinic-primary hover:underline break-all">
                          Visit Website
                        </a>
                      </div>
                    </div>
                  )}

                  {clinician.specialties && clinician.specialties.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-clinic-muted mb-2">Areas of Focus</p>
                      <div className="flex flex-wrap gap-2">
                        {clinician.specialties.map((tag) => (
                          <span key={tag} className="inline-flex items-center rounded-full bg-clinic-primary/10 text-clinic-primary-dark text-xs font-medium px-3 py-1">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </aside>
            </div>

            {/* Book Appointment CTA */}
            <div className="border-t border-clinic-primary-lighter pt-8 mt-8">
              <div className="bg-clinic-cream/50 rounded-card p-6 text-center border border-clinic-primary-lighter">
                <CalendarIcon className="mx-auto h-10 w-10 text-clinic-primary mb-3" />
                <h3 className="text-lg font-semibold text-clinic-dark font-body mb-2">
                  Ready to book an appointment?
                </h3>
                {isAuthenticated ? (
                  user?.role === 'patient' ? (
                    <>
                      <p className="text-clinic-muted mb-4">
                        View available times and schedule your appointment.
                      </p>
                      <Button
                        onClick={handleBookAppointment}
                        size="lg"
                        leftIcon={<CalendarIcon className="h-5 w-5" />}
                      >
                        Book Appointment
                      </Button>
                    </>
                  ) : (
                    <p className="text-clinic-muted">
                      Only patients can book appointments.
                    </p>
                  )
                ) : (
                  <>
                    <p className="text-clinic-muted mb-4">
                      Sign in or create an account to book an appointment.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        as={Link}
                        to={`/login?redirect=/patient/book/${clinician.booking_slug}`}
                        size="lg"
                      >
                        Sign In to Book
                      </Button>
                      <Button
                        as={Link}
                        to={`/register/patient?redirect=${encodeURIComponent(`/patient/book/${clinician.booking_slug}`)}`}
                        variant="secondary"
                        size="lg"
                      >
                        Create Account
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-clinic-primary-lighter mt-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center">
              <img
                src="/logos/logo-icon.png"
                alt="Clinic Studios"
                className="h-8 w-auto opacity-60"
              />
              <p className="ml-4 text-sm text-clinic-muted">
                &copy; {new Date().getFullYear()} Clinic Studios. All rights reserved.
              </p>
            </div>
            <div className="flex gap-4">
              <Link to="/privacy" className="text-sm text-clinic-muted hover:text-clinic-primary transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-sm text-clinic-muted hover:text-clinic-primary transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClinicianProfilePage;
